import { useState, useEffect } from 'react';

const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

const useFuelPrices = (regionCodes = ['NUS']) => {
  const [prices, setPrices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const codesKey = regionCodes.join(',');

  useEffect(() => {
    const fetchPrices = async () => {
      // Check localStorage cache
      const cacheKey = `fuel_prices_${codesKey}`;
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { timestamp, prices: cachedPrices, lastUpdated: cachedLastUpdated } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_TTL_MS) {
            setPrices(cachedPrices);
            setLastUpdated(cachedLastUpdated);
            setIsLoading(false);
            return;
          }
        }
      } catch {
        // ignore corrupt cache
      }

      setIsLoading(true);
      setError(null);
      try {
        const apiKey = process.env.REACT_APP_EIA_API_KEY;
        const duoareaParams = regionCodes.map(code => `facets[duoarea][]=${code}`).join('&');
        const url = `https://api.eia.gov/v2/petroleum/pri/gnd/data/?api_key=${apiKey}&frequency=weekly&data[0]=value&facets[product][]=EPMR&${duoareaParams}&sort[0][column]=period&sort[0][direction]=desc&offset=0&length=200`;

        const response = await fetch(url);
        if (!response.ok) throw new Error(`API error: ${response.status}`);
        const json = await response.json();

        const items = json?.response?.data || [];

        // Keep only the most recent entry per region
        const latestByRegion = {};
        for (const item of items) {
          const code = item.duoarea;
          if (!latestByRegion[code] || item.period > latestByRegion[code].period) {
            latestByRegion[code] = item;
          }
        }

        const formatted = Object.values(latestByRegion).map(item => ({
          regionCode: item.duoarea,
          name: item['area-name'],
          price: parseFloat(item.value),
          period: item.period,
        }));

        // Sort: national first, then alphabetically
        formatted.sort((a, b) => {
          if (a.regionCode === 'NUS') return -1;
          if (b.regionCode === 'NUS') return 1;
          return a.name.localeCompare(b.name);
        });

        const updatedPeriod = formatted.length > 0 ? formatted[0].period : null;

        // Save to cache
        try {
          localStorage.setItem(cacheKey, JSON.stringify({
            timestamp: Date.now(),
            prices: formatted,
            lastUpdated: updatedPeriod,
          }));
        } catch {
          // ignore storage errors (quota, etc.)
        }

        setPrices(formatted);
        if (updatedPeriod) setLastUpdated(updatedPeriod);
      } catch (err) {
        console.error('useFuelPrices error:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codesKey]);

  return { prices, isLoading, error, lastUpdated };
};

export default useFuelPrices;
